<template>
    <div
        class="overtime-tooltip absolute invisible opacity-0 group-hover/overtime:visible group-hover/overtime:opacity-100 group-hover/overtime:delay-200 transition-opacity duration-150 pointer-events-none p-3 text-left rounded top-3 right-0 z-50 whitespace-nowrap text-[11px]">
        <template v-if="mainShifts.length">
            <div class="mb-1 text-darkgray">Основная смена: {{ mainShiftLabel }}</div>
            <div v-if="!hrMainShift" v-for="shift in mainShifts" :key="shift.id" class="mb-2 text-gray text-[10px]">
                {{ getPositionName(shift) }}: {{ formatTime(shift.plan_work_start_at) }} - {{ formatTime(shift.plan_work_end_at) }}
            </div>
        </template>
        <template v-if="overtimeShifts.length">
            <div class="mb-1 text-darkgray">Дополнительная смена: {{ overtimeDuration }}</div>
            <div v-for="shift in overtimeShifts" :key="shift.id" class="mb-1 text-gray text-[10px]">
                {{ getPositionName(shift) }}: {{ formatTime(shift.plan_work_start_at) }} - {{ formatTime(shift.plan_work_end_at) }}
            </div>
        </template>
        <!-- RD-176: работа сотрудника в другом подразделении -->
        <div v-for="shift in otherDepartmentShifts" :key="shift.id" class="mb-1 text-darkgray">
            {{ formatOtherDepartmentShift(shift, departmentStore.departments) }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Schedule, Shift } from '@/types'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { formatOtherDepartmentShift } from '@/utils/otherDepartmentShifts'

dayjs.extend(utc)

interface Props {
    shifts: Shift[]
    schedule: Schedule
    otherDepartmentShifts?: Shift[]
}

const { shifts, schedule, otherDepartmentShifts = [] } = defineProps<Props>()
const scheduleStore = useScheduleStore()
const departmentStore = useDepartmentStore()

const mainShifts = computed(() => shifts.filter(s => !s.is_overtime))
const overtimeShifts = computed(() => shifts.filter(s => s.is_overtime))
const hrMainShift = computed(() => mainShifts.value.find((shift) => Boolean(shift.day_state_document)))

const getPositionName = (shift: Shift) =>
    scheduleStore.positions.find(p => p.id === shift.position)?.name
    ?? schedule.position?.name
    ?? ''

const formatTime = (time?: string | null) => time ? dayjs.utc(time).format('HH:mm') : '??:??'

const sumDuration = (shiftList: Shift[]) => {
    const total = shiftList.reduce((sum, s) => sum + (s.duration ? +s.duration : 0), 0)
    if (!total) return '—'
    const hours = Math.floor(total)
    const minutes = Math.round((total - hours) * 60)
    return minutes ? `${hours}ч ${minutes}м` : `${hours}ч`
}

const mainDuration = computed(() => sumDuration(mainShifts.value))
const overtimeDuration = computed(() => sumDuration(overtimeShifts.value))
const mainShiftLabel = computed(() => {
    if (!hrMainShift.value) return mainDuration.value

    return scheduleStore.dayTypes.find((item) => item.id === hrMainShift.value?.day_type)?.name || 'Кадровое отклонение'
})
</script>

<style scoped>
.overtime-tooltip {
    border: 1px solid rgb(255 255 255 / 0.58);
    background: rgb(255 255 255 / 0.5);
    box-shadow: 0 4px 12px oklch(0% 0 0 / 0.1);
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
}
</style>
